<script setup>
import { computed, onMounted, ref, watch } from "vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ArgonTable from "@/components/table/ArgonTable.vue";
import ArgonTd from "@/components/table/ArgonTd.vue";
import { eventStore } from "@/_store/ttmmEventStore";
import { argonTableStore } from "@/_store/argonTableStore";
import { userStore } from "@/_store/userStore";
import { errorUtils, fileUtils } from "@/utils";
import UserTypeBadge from "@/components/badges/UserType.vue";
import ModalEvent from "./ModalEvent.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import ActionsDropdown from "@/components/dropdown/ActionsDropdown.vue";
import ConfirmModal from "@/components/modal/ConfirmModal.vue";
import { useConfirmModal } from "@/composables/useConfirmModal";

const store = eventStore();
const tableStore = argonTableStore();
const userStores = userStore();

const snackbar = ref(null);
const showModal = ref(false);
const selectedEventId = ref(null);
const incidenceOnlyScopes = ["RECOGIDA", "ENTREGA"];
const eventTypeOptions = Object.freeze([
  { label: "Transito", value: "TRANSITO" },
  { label: "Reparto", value: "EN_REPARTO" },
  { label: "Incidencia", value: "INCIDENCIA" },
]);
const incidenceTypeOptions = Object.freeze([
  { label: "Incidencia", value: "INCIDENCIA" },
]);
const scopeOptions = Object.freeze([
  { label: "Recogida", value: "RECOGIDA" },
  { label: "Entrega", value: "ENTREGA" },
  { label: "Milla intermedia", value: "MILLA_INTERMEDIA" },
]);
const {
  showConfirmModal,
  confirmTitle,
  confirmText,
  confirmButtonText,
  cancelButtonText,
  confirmButtonClass,
  cancelButtonClass,
  confirmLoading,
  openConfirmModal,
  handleConfirmModal,
} = useConfirmModal();

const fieldFilters = ref([
  {
    field: "code",
    description: "Código",
  },
  {
    field: "description",
    description: "Descripción",
  }
]);
const defaultFilters = computed(() => [
  {
    field: "scope",
    label: "Ámbito",
    multi: true,
    options: scopeOptions,
  },
  {
    field: "type",
    label: "Tipo",
    multi: false,
    options: hasIncidenceOnlyScope(tableStore.filters)
      ? incidenceTypeOptions
      : eventTypeOptions,
  },
  {
    field: "entity",
    label: "Entidad",
    multi: true,
    options: [
      { label: "Parada", value: "PARADA" },
      { label: "Paquete", value: "PAQUETE" },
    ],
  },
  {
    field: "do_not_send",
    label: "No comunicar",
    multi: false,
    options: [
      { label: "Activo", value: true },
      { label: "No Activo", value: false },
    ],
  },
]);

const columns = ref([
  { header: "Código", field: "code", sort: false, position: "start" },
  {
    header: "Descripción",
    field: "description",
    sort: false,
    position: "start",
  },
  { header: "Tipo", field: "type", sort: false, position: "center" },
  { header: "Ámbito", field: "scope", sort: false, position: "center" },
  { header: "Entidad", field: "entity", sort: false, position: "center" },
  { header: "No comunicar", field: "do_not_send", sort: false, position: "center" },
  { header: "", field: "actions", sort: false, position: "center", width: "w-1" },
]);
const eventRows = computed(() => {
  if (Array.isArray(store.events?.data)) return store.events.data;
  if (Array.isArray(store.events?.events)) return store.events.events;
  if (Array.isArray(store.events)) return store.events;
  return [];
});
const selectedEvents = computed(() =>
  eventRows.value.filter((eventItem) =>
    tableStore.checkedList.includes(eventItem._id)
  )
);
const canDeleteSelected = computed(() => selectedEvents.value.length > 0);

onMounted(() => {
  tableStore.clear();
  const savedFilters = userStores.filtersPage.events?.filters || [];
  tableStore.changeFilters(getValidEventFilters(savedFilters));
  reloadPage();
});

function reloadPage() {
  userStores.filtersPage.events = {
    filters: tableStore.filters,
    filtersFavorite: tableStore.filtersFavorite,
  };

  store.getEvents(tableStore.pagination, tableStore.query, tableStore.sort);
}

function hasIncidenceOnlyScope(filters) {
  const scopeFilter = filters.find((filter) => filter.field === "scope");
  const scopes = scopeFilter?.value?.$in || [];

  return scopes.some((scope) => incidenceOnlyScopes.includes(scope));
}

function getValidEventFilters(filters) {
  if (!hasIncidenceOnlyScope(filters)) return filters;

  return filters.filter(
    (filter) => filter.field !== "type" || filter.value === "INCIDENCIA"
  );
}

function onFilterChanged(changes) {
  const validFilters = getValidEventFilters(changes);

  if (validFilters.length !== changes.length) {
    tableStore.changeFilters(validFilters);
  }

  reloadPage();
}

async function exportEvents() {
  const bytes = await store.exportar(
    tableStore.pagination,
    tableStore.query,
    tableStore.sort
  );

  fileUtils.downloadBytesFile(
    bytes,
    fileUtils.generateDownloadFileName("xlsx")
  );
}

function onSortChanged() {
  tableStore.clearPagination();
  reloadPage();
}

function getPosition(field) {
  const column = columns.value.find((col) => col.field === field);
  return column && column.position ? column.position : "start";
}
function openNewEventModal() {
  selectedEventId.value = null;
  showModal.value = true;
}

function editEvent(eventItem) {
  selectedEventId.value = eventItem?._id || null;
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
}

function successSaveEvent() {
  showModal.value = false;
  reloadPage();
}

async function deleteSelectedEvents() {
  if (!canDeleteSelected.value) return;

  openConfirmModal({
    title: "¿Está seguro de querer eliminar los eventos seleccionados?",
    text: "¡No podrá revertir esta acción!",
    confirmButtonText: "Eliminar",
    confirmButtonClass: "btn btn-xs ms-1 bg-gradient-danger",
    onConfirm: () => {
      const selectedIds = [...new Set(tableStore.checkedList)];
      const idsAsCsv = selectedIds.join(",");
      if (!idsAsCsv) return false;

      store.deleteEvent(idsAsCsv);
    },
  });
}

function deleteEventRow(eventItem) {
  const eventId = eventItem?._id;
  if (!eventId) return;

  openConfirmModal({
    title: "¿Está seguro de querer eliminar este evento?",
    text: "¡No podrá revertir esta acción!",
    confirmButtonText: "Eliminar",
    confirmButtonClass: "btn btn-xs ms-1 bg-gradient-danger",
    onConfirm: () => {
      store.deleteEvent(String(eventId));
    },
  });
}

function getLabel(scope) {
  return scope.replace("_", " ");
}
const closeSnackbar = () => {
  snackbar.value = null;
};

function getRowActionsMenuId(eventItem) {
  return `event-row-actions-${String(eventItem?._id || "unknown").replace(
    /[^a-zA-Z0-9_-]/g,
    "_"
  )}`;
}

function getRowActions() {
  return [
    [{ label: "Editar", event: "edit" }],
    [{ label: "Eliminar", event: "delete", type: "danger" }],
  ];
}

function onRowAction(eventItem, action) {
  const actionEvent = action?.event;
  if (!actionEvent || !eventItem) return;

  if (actionEvent === "edit") {
    editEvent(eventItem);
    return;
  }

  if (actionEvent === "delete") {
    deleteEventRow(eventItem);
  }
}

watch(
  () => store.eventDeleted,
  (value) => {
    if (value) {
      tableStore.clearSelection();
      reloadPage();
      snackbar.value = {
        title: "OK",
        message: "Evento eliminado",
        type: "info",
        color: "white",
      };
    }
  }
);

watch(
  () => store.error,
  (value) => {
    if (!value) return;
    if (showModal.value) return;
    snackbar.value = {
      title: "Error",
      message: errorUtils.getErrorMessageHtml(value),
      type: "danger",
      color: "white",
    };
  }
);
</script>

<template>
  <div class="card events-table-view">
    <div class="card-body events-table-body">
      <argon-table
        v-if="store.events"
        :columns="columns"
        :items="store.events"
        :field-filters="fieldFilters"
        :default-filters="defaultFilters"
        :show-config-option="true"
        :show-importar="false"
        :show-new-button="true"
        @filter-changed="onFilterChanged"
        @on-reload="reloadPage"
        @sort-changed="onSortChanged"
        @page-changed="reloadPage"
        @new-item="openNewEventModal"
        @on-click="editEvent"
        @export-data="exportEvents"
      >
        <template #buttons>
          <div
            class="dropdown d-inline"
            data-dropdown-static
            style="--dropdown-gap: 4px"
          >
            <argon-button
              id="eventActionsDropdown"
              color="primary"
              variant="outline"
              class="dropdown-toggle me-2 btn-xs"
              data-bs-toggle="dropdown"
              data-bs-display="static"
              aria-expanded="false"
            >
              <i class="fa fa-exchange me-sm-2"></i>Acciones
            </argon-button>
            <ul
              class="dropdown-menu z-index-1030 dropdown-menu-lg-start px-2 py-3"
              aria-labelledby="eventActionsDropdown"
            >
              <li>
                <a
                  class="dropdown-item border-radius-md"
                  :class="{ disabled: !canDeleteSelected }"
                  @click.prevent="deleteSelectedEvents"
                >
                  Eliminar
                </a>
              </li>
            </ul>
          </div>
        </template>
        <template #default="{ item }">
          <argon-td :position="getPosition('code')" :text-primary="item.code" />
          <argon-td
            :position="getPosition('description')"
            :text-primary="item.description"
          />
          <argon-td :position="getPosition('type')" >
            <user-type-badge
              style="min-width: 100px"
              class="badge badge-md"
              :item="getLabel(item.type)"
            />
          </argon-td>
          <argon-td
            :position="getPosition('scope')"
          >
          <user-type-badge
              style="min-width: 150px"
              class="badge badge-md"
              :item="getLabel(item.scope)"
            />
          </argon-td>
          <argon-td
            :position="getPosition('entity')"
          >
          <user-type-badge
              style="min-width: 100px"
              class="badge badge-md"
              :item="item.entity"
            />
          </argon-td>
          <argon-td :position="getPosition('do_not_send')">
            <i
              v-if="item.do_not_send === true"
              v-tooltip="'No comunicar a terceros'"
              class="fa fa-check grid-icon-primary text-xs"
              aria-label="No comunicar a terceros"
            ></i>
          </argon-td>
          <argon-td :position="getPosition('actions')">
            <div class="table-row-actions">
              <actions-dropdown
                :menu-id="getRowActionsMenuId(item)"
                :actions="getRowActions()"
                aria-label="Acciones del evento"
                trigger-class="table-action-button"
                menu-class="table-action-menu-dropdown"
                @action="(action) => onRowAction(item, action)"
              />
            </div>
          </argon-td>
        </template>
      </argon-table>
    </div>

    <modal-event
      v-if="showModal"
      :event-id="selectedEventId"
      @close="closeModal"
      @success="successSaveEvent"
    />
    <confirm-modal
      v-if="showConfirmModal"
      v-model="showConfirmModal"
      :title="confirmTitle"
      :text="confirmText"
      :confirm-button-text="confirmButtonText"
      :cancel-button-text="cancelButtonText"
      :confirm-button-class="confirmButtonClass"
      :cancel-button-class="cancelButtonClass"
      :loading="confirmLoading"
      @confirm="handleConfirmModal"
    />

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </div>
</template>

<style scoped>

:deep(thead th.text-center.w-1:last-child),
:deep(tbody td.text-center:last-child) {
  max-width: 48px;
  min-width: 48px;
  width: 48px;
}
</style>
